import ApiPricingVariables from "api/ApiPricingVariables"
import BodyText from "components/Typography/BodyText"
import { List } from "hooks/useHandleObjList"
import { PricingUnits } from "pages/purchase-plans/constants/defaultValues"
import { mapSimulatedMonthly } from "pages/purchase-plans/mappers/mapSummary"
import { memo, useMemo, useState } from "react"
import { useMutation } from "react-query"
import { useAppSelector } from "reducers/store"
import styled from "styled-components"
import { useDebounce } from "use-debounce"
import useDeepCompareEffect from "use-deep-compare-effect"
import { uniqueKey } from "utils/uniqueKey"
import DefaultSummary from "./DefaultSummary"

const ContentSummaryItemContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ResultContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

interface PlanSummaryProps {
  fromPrice?: string
  selectedProduct?: string
  pricingUnit?: PricingUnits
  pricingVariablesList: List<App.ISimplePricingVariable>
}

const MonthlySummary = (props: PlanSummaryProps) => {
  const { fromPrice = "", selectedProduct = "", pricingUnit, pricingVariablesList } = props

  const [monthlySummary, setMonthlySummary] = useState<App.ISimulatedConsumption>()

  const calculateMonthlySummary = useMutation({
    mutationFn: async (price: number) => {
      const data = await new ApiPricingVariables().calculateMonthlySummary({
        monthlyPrice: price,
        productId: selectedProduct
      })

      return data
    },
    onSuccess: (data) => {
      setMonthlySummary(data.data)
    }
  })

  const debouncedFromPrice = useDebounce([{ fromPrice }], 800)

  useDeepCompareEffect(() => {
    if (fromPrice && pricingUnit === PricingUnits.MONTHLY) {
      calculateMonthlySummary.mutate(+fromPrice)
    }
  }, [debouncedFromPrice])

  const isValid = !!monthlySummary?.totalPrice

  const eventTypeOptions = useAppSelector((state) => state.purchasePlan.eventTypeOptions.data.options)

  const eventTypeLabel = useMemo(() => {
    return Object.fromEntries(eventTypeOptions.map((eventType) => [eventType.value, eventType.label]))
  }, [eventTypeOptions])

  return (
    <>
      {!isValid && (
        <ResultContainer>
          <BodyText color="gray-medium" weight="semi-bold">
            Para gerar o resumo, preencha os campos em definições para precificação.
          </BodyText>
        </ResultContainer>
      )}

      {isValid && (
        <>
          <DefaultSummary
            fromPriceUnit="mês"
            totalPrice={mapSimulatedMonthly(monthlySummary?.totalPrice ?? 0)}
            label="Incluso no plano"
          >
            <>
              {pricingVariablesList?.map((pricingVariable) => (
                <ContentSummaryItemContainer key={uniqueKey()}>
                  <>
                    {!!pricingVariable.usageLimit && (
                      <BodyText size="small" weight="light" color="gray-dark">
                        {pricingVariable.usageLimit} {eventTypeLabel[pricingVariable.eventType]}
                      </BodyText>
                    )}
                  </>
                </ContentSummaryItemContainer>
              ))}
            </>
          </DefaultSummary>
        </>
      )}
    </>
  )
}

export default memo(MonthlySummary)
